/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

/**
 * Simple wrapper that overrides the default CDS tokens to style with the "Coach theme"
 * All children of this component will be styled with the overrides
 *
 * Any customizations that are not doable in CDS will be done in this
 * provider e.g. custom button behavior for active state.
 *
 * @example
 *
 * <CoachStylesProvider>
 *   <div className="coach-app">
 *     <Typography variant="h3semibold">
 *       Welcome to Coach!
 *     </Typography>
 *     <Button variant="primary">
 *       Some coach action
 *     </Button>
 *
 *     <IconButton variant="ghost" intent="close" />
 *   </div>
 * </CoachStylesProvider>
 */

const CoachStylesProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      data-theme="coach-web"
      css={css`
        /* CDS overrides - themeable approach to coach styling */
        --cds-color-interactive-primary: #3587fc; /* TODO: replace with new token */
        --cds-color-interactive-primary-hover: #1e72eb; /* TODO: replace with new token */
        --cds-color-interactive-secondary: #1e2229; /* TODO: replace with new token */

        .invert {
          --cds-color-neutral-primary-invert: #00255d;
          --cds-color-callouts-tertiary: #fff;
        }

        /* 
          Coach overrides - this should be temporary, ideally we shouldn't have 
          any overrides here and all overrides are purely token value updates.
        */
        .coach-button {
          --cds-border-radius-50: var(--cds-spacing-100); /* TODO: replace with new token */
          --cds-color-interactive-primary-pressed: var(--cds-color-interactive-primary-hover);

          transition: all 0.2s cubic-bezier(0, 1, 0.9, 1);

          &:active {
            transform: scale3d(0.98, 0.98, 0.98);
            border-radius: var(--cds-spacing-150);
          }

          &.coach-secondary-button {
            --cds-color-neutral-background-primary: transparent;
            --cds-color-interactive-background-primary-hover-weak: #3587fc1a; /* TODO: replace with new token */
            --cds-color-interactive-background-primary-pressed-weak: #3587fc1a; /* TODO: replace with new token */

            box-shadow: inset 0 0 0 2px #dae1ed; /* TODO: replace with new token */
            max-width: none; // override default 320px width

            &:hover,
            &:focus-visible {
              box-shadow: inset 0 0 0 2px #3587fc; /* TODO: replace with new token */
            }

            .cds-button-label {
              color: var(--cds-color-neutral-primary);
            }
          }
        }

        .coach-icon-button {
          --cds-border-radius-50: 24px; /* TODO: replace with new token */
          --cds-color-interactive-primary-pressed: var(--cds-color-interactive-primary-hover);
          --cds-color-interactive-background-primary-hover-weak: #3587fc1a; /* TODO: replace with new token */
          --cds-color-interactive-background-primary-pressed-weak: #3587fc1a; /* TODO: replace with new token */

          box-shadow: none;
          transition: all 0.2s cubic-bezier(0, 1, 0.9, 1);

          &:active {
            transform: scale3d(0.98, 0.98, 0.98);
          }

          &:hover,
          &:focus {
            box-shadow: inset 0 0 0 2px #3587fc; /* TODO: replace with new token */
          }

          svg {
            color: var(--cds-color-neutral-primary);
          }
        }
      `}
    >
      {children}
    </div>
  );
};

export default CoachStylesProvider;
